import { useTranslation } from "react-i18next";
import { IconLoader2 } from "@tabler/icons-react";
import BottomPanel from "../app/panels/BottomPanel";
import { DesktopDialog } from "../app/DesktopDialog";
import { ChatSourceIdentity, type ChatSource } from "./ChatSources";
import { SourceLink } from "./SourceLink";
import { SignalMetric, SourceQualityExplanation } from "./SourceSignals";
import {
  StudyAppraisalLedger,
  JournalStandingSection,
  type ShownAppraisal,
  type ReadingLanguage,
  type JournalStanding,
} from "../works-score/WorksScoreScreen";

export type ChatSourceSheetContent = {
  source: ChatSource;
  /**
   * What Juisci knows about the venue. Null on every source with no Juisci
   * journal row behind it, which today is every PubMed and web snapshot:
   * those carry a journal NAME, not the ranked journal itself. "loading" while
   * the read that states it is in flight: a placeholder holds its place.
   */
  journal: JournalStanding | "loading" | null;
  /**
   * The level of evidence the source's own design and appraisal earn it, and
   * the language the reading is stated in. Null on every source with no Juisci
   * Work behind it (a web or PubMed snapshot carries no design), and on a Work
   * whose level is not settled yet.
   *
   * One field and not two, because the appraisal is read in the language of the
   * ARTICLE it came from, and a sheet holding one without the other could state
   * a French reading under English questions. "loading" while the article read
   * is in flight.
   */
  level:
    { appraisal: ShownAppraisal; language: ReadingLanguage } | "loading" | null;
};

/**
 * One cited source, opened from a row of `ChatSources` (Figma node
 * 6984:14562). It states the source's full identity, where to read it, and what
 * Juisci knows about the journal it came out in.
 *
 * `content` is null while the sheet animates shut, and on a source that no
 * longer resolves, so the panel is told whether it is open separately from what
 * it holds. It is "loading" while the source is still being read, and "failed"
 * when that read failed.
 *
 * A bottom sheet on a phone; a `DesktopDialog` on a desktop.
 */
export function ChatSourceSheet({
  content,
  citationNumber,
  presentation,
  isOpen,
  onClose,
}: {
  content: ChatSourceSheetContent | "loading" | "failed" | null;
  /**
   * The number the answer's inline citation chip points at. Null only where a
   * source is opened outside a numbered answer, which then reads "Source".
   */
  citationNumber: number | null;
  presentation: "sheet" | "dialog";
  isOpen: boolean;
  onClose: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const title =
    citationNumber === null
      ? t("Source")
      : t("Source {{n}}", { n: citationNumber });
  if (presentation === "dialog") {
    if (!isOpen) return null;
    return (
      <DesktopDialog
        presentation="modal"
        title={title}
        header={{ layout: "inline" }}
        subtitle={null}
        onClose={onClose}
        footer={null}
      >
        <SourceBody content={content} presentation="dialog" />
      </DesktopDialog>
    );
  }
  return (
    <BottomPanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      displayCloseButton
    >
      <SourceBody content={content} presentation="sheet" />
    </BottomPanel>
  );
}

const PULSE = "animate-pulse rounded-full bg-ds-mint-200";

// Holds a block's place while its read is in flight, so the sheet does not jump when it lands.
function BlockPlaceholder({
  block,
}: {
  block: "journal" | "study";
}): React.JSX.Element {
  switch (block) {
    case "journal":
      return (
        <div
          aria-hidden
          data-testid="source-journal-placeholder"
          className="flex flex-col gap-3"
        >
          <span className={`h-3 w-16 ${PULSE}`} />
          <span className="flex items-center justify-between">
            <span className={`h-8 w-14 ${PULSE}`} />
            <span className={`size-10 ${PULSE}`} />
          </span>
          <span className={`h-4 w-full ${PULSE}`} />
          <span className={`h-4 w-full ${PULSE}`} />
          <span className={`h-4 w-2/3 ${PULSE}`} />
        </div>
      );
    case "study":
      return (
        <div
          aria-hidden
          data-testid="source-study-placeholder"
          className="flex flex-col gap-3 pb-2"
        >
          <span className={`h-3 w-14 ${PULSE}`} />
          <span className="h-24 w-full animate-pulse rounded-xl bg-ds-mint-200" />
        </div>
      );
    default:
      throw new Error(block satisfies never);
  }
}

// The dialog's card holds its own margins; the sheet's body brings them.
// Only the signals the api states: an unknown value is not drawn as a zero.
// A placeholder holds their place while the api still resolves the source.
function SignalsSection({
  source,
}: {
  source: ChatSource;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { signals } = source;
  if (signals.quality === null && signals.endorsement === null)
    return source.resolution === "pending" ? (
      <>
        <hr className="m-0 border-t border-ds-slate-100" />
        <div
          aria-hidden
          data-testid="source-signals-placeholder"
          className="flex flex-col gap-2 rounded-xl bg-ds-slate-50 px-3 py-2"
        >
          <span className={`h-5 w-full ${PULSE}`} />
          <span className={`h-5 w-full ${PULSE}`} />
        </div>
      </>
    ) : null;
  return (
    <>
      <hr className="m-0 border-t border-ds-slate-100" />
      <div className="flex flex-col divide-y divide-ds-slate-100 rounded-xl bg-ds-slate-50 px-3 py-1">
        {signals.quality !== null && (
          <SignalMetric
            label={t("Source quality")}
            value={signals.quality}
            info={<SourceQualityExplanation />}
          />
        )}
        {signals.endorsement !== null && (
          <SignalMetric
            label={t("Endorsement")}
            value={signals.endorsement}
            info={t("How widely the field has cited this work.")}
          />
        )}
      </div>
    </>
  );
}

function SourceBody({
  content,
  presentation,
}: {
  content: ChatSourceSheetContent | "loading" | "failed" | null;
  presentation: "sheet" | "dialog";
}): React.JSX.Element {
  const { t } = useTranslation();
  const isSheet = presentation === "sheet";
  const notice = isSheet
    ? "px-5 py-6 text-footnote text-ds-slate-600"
    : "m-0 text-footnote text-ds-slate-600";
  return (
    <>
      {content === "loading" ? (
        <div className="flex justify-center py-6">
          <IconLoader2
            className="size-5 animate-spin text-ds-slate-400"
            stroke={1.5}
            aria-label={t("Loading…")}
          />
        </div>
      ) : content === "failed" ? (
        <p className={notice}>{t("Couldn't load this source.")}</p>
      ) : content === null ? (
        <p className={notice}>{t("This source is no longer available.")}</p>
      ) : (
        <div
          className={
            isSheet
              ? "flex flex-col gap-3 px-5 pt-5 pb-6 font-dm-sans"
              : "flex w-full flex-col gap-3 font-dm-sans"
          }
        >
          <div className="flex items-end gap-2">
            <ChatSourceIdentity source={content.source} size="sheet" />
            {content.source.target !== null && (
              <SourceLink target={content.source.target} />
            )}
          </div>
          {content.journal !== null && (
            <>
              <hr className="m-0 border-t border-ds-slate-100" />
              {content.journal === "loading" ? (
                <BlockPlaceholder block="journal" />
              ) : (
                <JournalStandingSection {...content.journal} />
              )}
            </>
          )}
          {content.level !== null && (
            <>
              <hr className="m-0 border-t border-ds-slate-100" />
              {content.level === "loading" ? (
                <BlockPlaceholder block="study" />
              ) : (
                <StudyAppraisalLedger
                  level={content.level.appraisal}
                  language={content.level.language}
                />
              )}
            </>
          )}
          <SignalsSection source={content.source} />
        </div>
      )}
    </>
  );
}
